import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { HerstellerUebersicht, KategorieKnoten, ProduktSeite } from "@repo/shared";

import { api, errorMessage } from "../lib/api";
import { ProduktKonfigurator } from "../components/ProduktKonfigurator";

const SEITE = 30;

/** Sucht einen Knoten samt seinem Pfad von der Wurzel aus. */
function pfadZu(baum: KategorieKnoten[], id: string): KategorieKnoten[] {
  for (const knoten of baum) {
    if (knoten.id === id) return [knoten];
    const unten = pfadZu(knoten.unterkategorien, id);
    if (unten.length > 0) return [knoten, ...unten];
  }
  return [];
}

/**
 * Assistent entlang der API-Hierarchie: Hersteller → Kategorie → Produkt →
 * Konfiguration. Der Stand steckt in der URL, damit Zurück und Neuladen
 * funktionieren.
 */
export function KonfiguratorPage() {
  const [params, setParams] = useSearchParams();
  const herstellerKey = params.get("hersteller") ?? "";
  const kategorieId = params.get("kategorie") ?? "";
  const produktId = params.get("produkt") ?? "";
  const suche = params.get("suche") ?? "";
  const offset = Number(params.get("offset") ?? 0);

  const [hersteller, setHersteller] = useState<HerstellerUebersicht[]>([]);
  const [kategorien, setKategorien] = useState<KategorieKnoten[]>([]);
  const [produkte, setProdukte] = useState<ProduktSeite | null>(null);
  const [eingabe, setEingabe] = useState(suche);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [geladenerName, setGeladenerName] = useState<string | null>(null);
  // Merkt sich je Hersteller/Kategorie, ob es ohne Suche mehr als ein Produkt
  // gibt (null = noch unbekannt). So flackert das Suchfeld nicht, wenn die
  // Liste beim Leeren der Suche kurz neu lädt.
  const [mehrAlsEinProdukt, setMehrAlsEinProdukt] = useState<boolean | null>(null);

  /** Setzt Parameter; ein Wechsel weiter oben verwirft alles darunter. */
  function gehe(neu: Record<string, string>) {
    const naechste = new URLSearchParams(params);
    const reihenfolge = ["hersteller", "kategorie", "produkt"];
    const tiefste = Math.min(...Object.keys(neu).map((k) => reihenfolge.indexOf(k)).filter((i) => i >= 0));
    if (Number.isFinite(tiefste)) {
      for (const k of reihenfolge.slice(tiefste + 1)) naechste.delete(k);
      naechste.delete("offset");
      if (tiefste < 2) naechste.delete("suche");
    }
    for (const [k, v] of Object.entries(neu)) {
      if (v) naechste.set(k, v);
      else naechste.delete(k);
    }
    setParams(naechste);
  }

  // 1 · Hersteller
  useEffect(() => {
    void (async () => {
      const res = await api.api.admin.katalog.hersteller.$get();
      if (!res.ok) return setFehler(await errorMessage(res, "Hersteller nicht ladbar"));
      const daten = await res.json();
      if ("hersteller" in daten) setHersteller(daten.hersteller);
    })();
  }, []);

  const aktiverHersteller = hersteller.find((h) => h.schluessel === herstellerKey);

  // 2 · Kategorien
  useEffect(() => {
    setKategorien([]);
    if (!herstellerKey) return;
    void (async () => {
      const res = await api.api.admin.katalog.hersteller[":hersteller"].kategorien.$get({
        param: { hersteller: herstellerKey },
      });
      if (!res.ok) return setFehler(await errorMessage(res, "Kategorien nicht ladbar"));
      const daten = await res.json();
      if ("kategorien" in daten) setKategorien(daten.kategorien);
    })();
  }, [herstellerKey]);

  const pfad = useMemo(() => (kategorieId ? pfadZu(kategorien, kategorieId) : []), [kategorien, kategorieId]);
  const aktuelleKategorie = pfad.at(-1);
  const unterkategorien = aktuelleKategorie ? aktuelleKategorie.unterkategorien : kategorien;

  // Produkte direkt in einer Kategorie, die auch Unterkategorien hat (Protokoll 3) –
  // sie stehen auf derselben Seite unter den Unterkategorien.
  const nurDirekte =
    !suche && unterkategorien.length > 0 && (aktuelleKategorie?.anzahlDirekt ?? 0) > 0;

  // Produkte zeigen, sobald es keine tiefere Ebene mehr gibt, die Kategorie eigene
  // Produkte hat – oder der Hersteller gar keine Kategorien kennt.
  const zeigeProdukte =
    Boolean(herstellerKey) &&
    (aktiverHersteller?.hatKategorien === false || unterkategorien.length === 0 || Boolean(suche) || nurDirekte);

  useEffect(() => setMehrAlsEinProdukt(null), [herstellerKey, kategorieId]);

  // 3 · Produkte
  useEffect(() => {
    setProdukte(null);
    if (!herstellerKey || !zeigeProdukte || produktId) return;
    let abgebrochen = false;
    setLaedt(true);
    void (async () => {
      try {
        const res = await api.api.admin.katalog.hersteller[":hersteller"].produkte.$get({
          param: { hersteller: herstellerKey },
          query: {
            ...(kategorieId && aktiverHersteller?.hatKategorien ? { kategorie: kategorieId } : {}),
            ...(nurDirekte ? { direkt: "true" as const } : {}),
            ...(suche ? { suche } : {}),
            limit: String(SEITE),
            offset: String(offset),
          },
        });
        if (!res.ok) throw new Error(await errorMessage(res, "Produkte nicht ladbar"));
        const daten = await res.json();
        if (!abgebrochen && "produkte" in daten) {
          setProdukte(daten);
          if (!suche) setMehrAlsEinProdukt(daten.gesamt > 1);
        }
      } catch (cause) {
        if (!abgebrochen) setFehler(cause instanceof Error ? cause.message : "Fehler");
      } finally {
        if (!abgebrochen) setLaedt(false);
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [herstellerKey, kategorieId, suche, offset, zeigeProdukte, nurDirekte, produktId, aktiverHersteller?.hatKategorien]);

  useEffect(() => setEingabe(suche), [suche]);

  // Suchfeld nur, wenn es etwas zu suchen gibt: aktive Suche (sonst ließe sie
  // sich nicht löschen), Unterkategorien oder mehr als ein Produkt. Solange
  // die Produktzahl unbekannt ist, bleibt es verborgen.
  const zeigeSuche =
    Boolean(suche) ||
    (Boolean(aktiverHersteller?.hatKategorien) && unterkategorien.length > 0) ||
    mehrAlsEinProdukt === true;

  const produktName = produkte?.produkte.find((p) => p.id === produktId)?.name ?? geladenerName;

  return (
    <section>
      <h1>Konfigurator</h1>
      <p className="muted">
        Folgt der API-Hierarchie: Hersteller → Kategorie → Produkt → Konfiguration. Diese Seite
        kennt keinen einzigen Hersteller – sie rendert ausschließlich, was die API liefert.
      </p>

      <nav className="schritte" aria-label="Fortschritt">
        <button type="button" className="link" onClick={() => gehe({ hersteller: "" })}>
          Hersteller
        </button>
        {aktiverHersteller && (
          <>
            <span>›</span>
            <button type="button" className="link" onClick={() => gehe({ kategorie: "" })}>
              {aktiverHersteller.name}
            </button>
          </>
        )}
        {pfad.map((k) => (
          <span key={k.id}>
            <span>› </span>
            <button type="button" className="link" onClick={() => gehe({ kategorie: k.id })}>
              {k.name}
            </button>
          </span>
        ))}
        {produktId && (
          <>
            <span>›</span>
            <strong>{produktName ?? produktId}</strong>
          </>
        )}
      </nav>

      {fehler && <p className="error">{fehler}</p>}

      {/* 1 · Hersteller */}
      {!herstellerKey && (
        <div className="kacheln">
          {hersteller.map((h) => (
            <button
              key={h.schluessel}
              type="button"
              className="kachel"
              onClick={() => gehe({ hersteller: h.schluessel })}
            >
              <strong>{h.name}</strong>
              <span className="muted small">{h.beschreibung}</span>
              <span className="small">{h.anzahlProdukte.toLocaleString("de-DE")} Produkte</span>
            </button>
          ))}
        </div>
      )}

      {/* 2 · Kategorien */}
      {herstellerKey && !produktId && !suche && aktiverHersteller?.hatKategorien && unterkategorien.length > 0 && (
        <div className="kacheln">
          {unterkategorien.map((k) => (
            <button key={k.id} type="button" className="kachel" onClick={() => gehe({ kategorie: k.id })}>
              {k.bildUrl && (
                <img
                  src={k.bildUrl}
                  alt=""
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.hidden = true;
                  }}
                />
              )}
              <strong>{k.name}</strong>
              <span className="small">
                {k.anzahlProdukte} Produkte
                {k.unterkategorien.length > 0 ? ` · ${k.unterkategorien.length} Unterkategorien` : ""}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Suche auf jeder Ebene, sofern es etwas zu suchen gibt */}
      {herstellerKey && !produktId && zeigeSuche && (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            const naechste = new URLSearchParams(params);
            if (eingabe) naechste.set("suche", eingabe);
            else naechste.delete("suche");
            naechste.delete("offset");
            setParams(naechste);
          }}
        >
          <input
            type="search"
            placeholder={`In ${aktuelleKategorie?.name ?? aktiverHersteller?.name ?? "Katalog"} suchen`}
            value={eingabe}
            onChange={(e) => setEingabe(e.target.value)}
          />
          <button type="submit">Suchen</button>
        </form>
      )}

      {/* 3 · Produkte */}
      {zeigeProdukte && !produktId && (
        <>
          {laedt && <p className="muted">Lade Produkte …</p>}
          {produkte && (
            <>
              <p className="muted small">
                {produkte.gesamt.toLocaleString("de-DE")} Produkte
                {nurDirekte && aktuelleKategorie ? ` direkt in ${aktuelleKategorie.name}` : ""}
              </p>
              <div className="produktliste">
                {produkte.produkte.map((p) => (
                  <button key={p.id} type="button" className="produktzeile" onClick={() => gehe({ produkt: p.id })}>
                    {p.bildUrl ? (
                      <img
                        src={p.bildUrl}
                        alt=""
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.visibility = "hidden";
                        }}
                      />
                    ) : (
                      <span className="bild-platzhalter" />
                    )}
                    <span>
                      <strong>{p.name}</strong>
                      <span className="muted small">
                        {[p.variante, p.beschreibung].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
              {produkte.gesamt > SEITE && (
                <div className="actions blaetterung">
                  <button
                    type="button"
                    disabled={offset === 0}
                    onClick={() => {
                      const n = new URLSearchParams(params);
                      n.set("offset", String(Math.max(0, offset - SEITE)));
                      setParams(n);
                    }}
                  >
                    Zurück
                  </button>
                  <span className="muted small">
                    {offset + 1}–{Math.min(offset + SEITE, produkte.gesamt)} von {produkte.gesamt}
                  </span>
                  <button
                    type="button"
                    disabled={offset + SEITE >= produkte.gesamt}
                    onClick={() => {
                      const n = new URLSearchParams(params);
                      n.set("offset", String(offset + SEITE));
                      setParams(n);
                    }}
                  >
                    Weiter
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* 4 · Konfiguration */}
      {herstellerKey && produktId && (
        <ProduktKonfigurator hersteller={herstellerKey} produktId={produktId} onGeladen={setGeladenerName} />
      )}
    </section>
  );
}
